import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import snakecaseKeys from 'snakecase-keys';
import _ from 'helpers/no_conflict_lodash';

import { Table } from 'react-bootstrap';

import { clickAsLink } from 'helpers/link_helpers';
import MergeRecordsModal from 'components/modals/merge_records_modal';
import AlertDismissable from 'components/alerts/alert_dismissable';

import 'components/styles/merge_page.scss';

// NOT exported
const companyAttributes = [
  'name',
  'url',
  'email',
  'facebookUrl',
  'twitterUrl',
  'skypeName',
  'referralSource',
  'industry'
];

export default class MergeCompanies extends React.Component {
  static propTypes = {
    companies: PropTypes.array.isRequired,
    companiesIndex: PropTypes.string.isRequired,
    mergeUrl: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    const keys = {};

    companyAttributes.map((key) => {
      if (this.shouldDisplayRow(key)) {
        keys[key] = null;
      }
    });

    this.state = {
      showMergeModal: false,
      showErrorPane: false,
      keys: keys
    };
    this.errors = [];
    this.requiredFields = [];
  }

  findSelectedCompany = (id) => {
    return this.props.companies.find(company => company.id === id);
  }

  handleCancelModal = () => {
    this.setState({ showMergeModal: false });
  }

  handleErrorPaneDismiss = () => {
    this.setState({ showErrorPane: false });
  }

  handleSubmit = () => {
    const keys = this.state.keys;
    companyAttributes.map((key) => {
      if (!this.shouldDisplayRow(key)) {
        keys[key] = this.props.companies[0].id;
      }
    });

    return axios.post(this.props.mergeUrl, {
      mappings: snakecaseKeys(keys),
      company_ids: this.props.companies.map(company => company.id)
    }).then((response) => {
      window.location = response.data.nextUrl;
    }).catch((error) => {
      this.errors = error.response.data.errors;
      this.showErrorPane();
      this.handleCancelModal();
    });
  }

  onSelectAll = (e) => {
    const id = parseInt(e.target.value);
    const keys = {};

    companyAttributes.map((key) => {
      keys[key] = id;
    });

    this.setState({ keys: keys });
  }

  onSelectKey = (id, key) => {
    this.setState(prevState => ({
      keys: {
        ...prevState.keys,
        [key]: parseInt(id)
      }
    }));
  }

  requiredKeysSelected = () => {
    const required = this.requiredFields.every((key) => {
      if (this.shouldDisplayRow(key)) {
        return this.state.keys[key] != null;
      } else {
        return true;
      }
    });

    return required;
  }

  selectedAll = (id) => {
    const selected = Object.keys(this.state.keys).every((key) => {
      return this.state.keys[key] === parseInt(id);
    });
    return selected;
  }

  shouldDisplayRow(key) {
    const value =
      Array.isArray(this.props.companies[0][key])
        ? this.props.companies[0][key].toString()
        : this.props.companies[0][key];

    let retVal = false;

    this.props.companies.map((company, index) => {
      const val = Array.isArray(company[key]) ? company[key].toString() : company[key];
      if (index > 0 && value != val && (value || val)) {
        retVal = true;
      }
    });

    return retVal;
  }

  showErrorPane = () => {
    this.setState({ showErrorPane: true });
  }

  showMergeModal = () => {
    this.setState({ showMergeModal: true });
  }

  showCompanyIndex = (e) => {
    clickAsLink(window, this.props.companiesIndex, e);
  }

  render() {
    const disabled = !this.requiredKeysSelected();
    return (
      <div className="container">
        <div className="page-header">
          <div className="clickable preview-link" onClick={this.showCompanyIndex}>
            &lt; Back to Companies
          </div>
          <h3>
            Merge Companies
          </h3>
        </div>
        <div className="container merge-container white border-top-0">
          {this.renderErrorPane()}
          <p className="text-lg">
            In the table below, you will see all conflicting information
            between the companies you have selected to merge. For each
            row, please select which data you would like to keep.
          </p>
          <p className="text-lg">
            Please note that all associations will be preserved.
          </p>
          <div>
            <Table className="merge-table">
              <thead>
                {this.renderHeaderColumns()}
              </thead>
              <tbody>
                {this.renderTableContent()}
              </tbody>
            </Table>
            <div className="merge-buttons">
              <button
                className="btn btn-sm btn-cancel"
                onClick={this.showCompanyIndex}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-save left-buffer-md"
                disabled={disabled}
                onClick={this.showMergeModal}
              >
                Merge
              </button>
            </div>
            {this.renderMergeModal()}
          </div>
        </div>
      </div>
    );
  }

  renderHeaderColumns() {
    const companyHeaders = [];
    this.props.companies.map((company, index) => {
      companyHeaders.push(
        <th key={index}>
          <input
            checked={this.selectedAll(company.id)}
            className="checkbox--md"
            type="checkbox"
            value={company.id}
            onChange={this.onSelectAll}
          />
          <span className="left-buffer-md"> Company {index + 1} </span>
        </th>
      );
    });

    return (
      <tr>
        <th> Fields </th>
        {companyHeaders}
        <th> Merged Company </th>
      </tr>
    );
  }

  renderTableContent() {
    const rows = [];
    const keysToDelete = [];
    companyAttributes.map((key) => {
      if (this.shouldDisplayRow(key)) {
        const companyCells = [];
        this.props.companies.map((company) => {
          const value = Array.isArray(company[key]) ? company[key].join(', ') : company[key];

          companyCells.push(
            <td key={company.id + key}>
              <input
                checked={this.state.keys[key] === company.id}
                className="checkbox--md"
                key={key}
                type="checkbox"
                value={company.id}
                onChange={() => this.onSelectKey(company.id, key)}
              />
              <span className="left-buffer-sm">
                {value}
              </span>
            </td>
          );
        });

        const id = this.state.keys[key];
        const company = this.findSelectedCompany(id);
        const newVal = id ? (company[key] || '') : '';

        rows.push(
          <tr key={key}>
            <td>
              {_.startCase(key)}
              {this.requiredFields.includes(key) && <span className="required"> * </span> }
            </td>
            {companyCells}
            <td> {newVal} </td>
          </tr>
        );
      } else keysToDelete.push(key);
    });
    return rows;
  }

  renderMergeModal() {
    return (
      <MergeRecordsModal
        recordType={'Companies'}
        shown={this.state.showMergeModal}
        onCancel={this.handleCancelModal}
        onSubmit={this.handleSubmit}
      />
    );
  }

  renderErrorPane() {
    return (
      <AlertDismissable handleDismiss={this.handleErrorPaneDismiss} shown={this.state.showErrorPane}>
        <strong>Unable to merge companies</strong>
        <ul>
          {this.errors.map((error, index) => <li key={index}>{error}</li>)}
        </ul>
      </AlertDismissable>
    );
  }

}
